/**
 * @fileoverview API service for Gain Customers feature.
 * Calls Express proxy for Reddit/PetMedAI/AI, and Deals_Backend for lead CRUD.
 */

import { BE_URLS } from "@/constants/config.js";
import axios from "@/api/axios/axios_core_deal.js";

const DEAL_HOST = BE_URLS.BACKEND_DEAL_SERVER_URL;
const PROXY_HOST = process.env.VUE_APP_EXPRESS_PROXY_URL || '';
const EXPRESS_API_KEY = process.env.VUE_APP_EXPRESS_API_KEY || '';

// Common headers for Express proxy calls
const proxyHeaders = {
  'Content-Type': 'application/json',
  'x-api-key': EXPRESS_API_KEY,
};

// ==========================================
// Reddit (via Express proxy)
// ==========================================

/**
 * Search Reddit for posts matching a query in a subreddit.
 * @param {string} query - Search query
 * @param {string} subreddit - Subreddit name (without r/)
 * @param {string} sort - Sort order (new, relevance, hot, top)
 * @param {number} limit - Max results
 * @returns {Promise} Array of posts
 */
export async function searchReddit(query, subreddit = 'AskVet', sort = 'new', limit = 25) {
  const response = await fetch(`${PROXY_HOST}/api/reddit/search`, {
    method: 'POST',
    headers: proxyHeaders,
    body: JSON.stringify({ query, subreddit, sort, limit })
  });
  if (!response.ok) throw new Error('Reddit search failed');
  return response.json();
}

/**
 * Fetch recent posts from a subreddit (no search query).
 * @param {string} subreddit - Subreddit name (without r/)
 * @param {number} limit - Max results (default 50)
 * @param {string} after - Pagination cursor
 * @returns {Promise} { posts, after }
 */
export async function fetchRecentPosts(subreddit = 'AskVet', limit = 500, force = false) {
  const params = new URLSearchParams({ subreddit, limit });
  if (force) params.append('force', 'true');
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 330000); // 5.5 min timeout (200 posts via Apify can take 2-5 min)
  try {
    const response = await fetch(`${PROXY_HOST}/api/reddit/recent?${params}`, {
      headers: proxyHeaders,
      signal: controller.signal,
    });
    if (!response.ok) throw new Error('Fetch recent posts failed');
    return response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Fetch recent posts timed out — the Apify request took too long (>5 min)');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * AI batch filter — identify posts where someone describes pet health symptoms.
 * @param {Array} posts - Array of post objects (id, title, body)
 * @param {string} petType - Pet type filter ('cat', 'dog', 'any')
 * @returns {Promise} { total, relevantCount, relevantPosts }
 */
export async function filterRelevantPosts(posts, petType = 'any') {
  const response = await fetch(`${PROXY_HOST}/api/ai/filter-relevant-posts`, {
    method: 'POST',
    headers: proxyHeaders,
    body: JSON.stringify({ posts, petType })
  });
  if (!response.ok) throw new Error('AI filter failed');
  return response.json();
}

/**
 * Generate a natural award hook message based on the post and assessment.
 * The message + shareUrl combined must fit within ~100 characters.
 * @param {string} shareUrl - The share URL to append (AI budgets chars around it)
 */
export async function generateHook(postTitle, postBody, petType, mainSymptom, shareUrl) {
  const response = await fetch(`${PROXY_HOST}/api/ai/generate-hook`, {
    method: 'POST',
    headers: proxyHeaders,
    body: JSON.stringify({ postTitle, postBody, petType, mainSymptom, shareUrl })
  });
  if (!response.ok) throw new Error('Generate hook failed');
  return response.json();
}

// ==========================================
// AI Post Parser (via Express proxy)
// ==========================================

/**
 * Extract pet info from a Reddit post using AI.
 * @param {string} title - Post title
 * @param {string} body - Post body
 * @param {string} subreddit - Subreddit name (for context hint)
 * @param {string} subredditDescription - Subreddit description (for context hint)
 * @returns {Promise} Parsed pet info
 */
export async function parsePetPost(title, body, subreddit, subredditDescription) {
  const response = await fetch(`${PROXY_HOST}/api/ai/parse-pet-post`, {
    method: 'POST',
    headers: proxyHeaders,
    body: JSON.stringify({ title, body, subreddit, subredditDescription })
  });
  if (!response.ok) throw new Error('AI parse failed');
  return response.json();
}

// ==========================================
// PetMedAI (via Express proxy)
// ==========================================

/**
 * Analyze pet images using GPT-4 Vision before diagnosis.
 * @param {Array} images - Array of { imageUrl, petInfo, context }
 * @returns {Promise} Array of image analysis results
 */
export async function analyzeImages(images) {
  const response = await fetch(`${PROXY_HOST}/api/petmedai/analyze-images`, {
    method: 'POST',
    headers: proxyHeaders,
    body: JSON.stringify({ images })
  });
  if (!response.ok) throw new Error('Image analysis failed');
  return response.json();
}

/**
 * Analyze pet videos before diagnosis.
 * @param {Array} videos - Array of { videoUrl, petInfo, context }
 * @returns {Promise} Array of video analysis results
 */
export async function analyzeVideos(videos) {
  const response = await fetch(`${PROXY_HOST}/api/petmedai/analyze-videos`, {
    method: 'POST',
    headers: proxyHeaders,
    body: JSON.stringify({ videos })
  });
  if (!response.ok) throw new Error('Video analysis failed');
  return response.json();
}

/**
 * Run a PetMedAI diagnosis.
 * @param {Object} payload - Diagnosis payload (pet, symptoms, etc.)
 * @returns {Promise} Diagnosis result
 */
export async function runDiagnosis(payload) {
  const response = await fetch(`${PROXY_HOST}/api/petmedai/diagnosis`, {
    method: 'POST',
    headers: proxyHeaders,
    body: JSON.stringify(payload)
  });
  if (!response.ok) throw new Error('Diagnosis failed');
  return response.json();
}

/**
 * Save a health assessment in PetMedAI.
 * @param {Object} payload - Assessment data
 * @returns {Promise} Saved assessment
 */
export async function saveAssessment(payload) {
  const response = await fetch(`${PROXY_HOST}/api/petmedai/save-assessment`, {
    method: 'POST',
    headers: proxyHeaders,
    body: JSON.stringify(payload)
  });
  if (!response.ok) throw new Error('Save assessment failed');
  return response.json();
}

/**
 * Create a share link for an assessment.
 * @param {string} assessmentId
 * @param {string} referralSource
 * @param {string} referralId
 * @returns {Promise} Share link info
 */
export async function createShareLink(assessmentId, referralSource, referralId) {
  const response = await fetch(`${PROXY_HOST}/api/petmedai/create-share`, {
    method: 'POST',
    headers: proxyHeaders,
    body: JSON.stringify({ assessmentId, duration: '2m', referralSource, referralId })
  });
  if (!response.ok) throw new Error('Create share link failed');
  return response.json();
}

/**
 * Get share link stats (viewCount, etc.)
 * @param {string} assessmentId
 * @returns {Promise} Share stats
 */
export async function getShareStats(assessmentId) {
  const response = await fetch(`${PROXY_HOST}/api/petmedai/share-stats/${assessmentId}`, {
    headers: proxyHeaders,
  });
  if (!response.ok) throw new Error('Get share stats failed');
  return response.json();
}

/**
 * Give a Reddit award on a post via backend Playwright automation.
 * @param {string} postUrl - Full Reddit post URL
 * @param {string} message - Award message text
 * @returns {Promise} { success: true } or error
 */
export async function giveRedditAward(postUrl, message) {
  const response = await fetch(`${PROXY_HOST}/api/reddit/award`, {
    method: 'POST',
    headers: proxyHeaders,
    body: JSON.stringify({ postUrl, message })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Award failed' }));
    throw new Error(err.error || 'Award failed');
  }
  return response.json();
}

// ==========================================
// Leads CRUD (via Deals_Backend)
// ==========================================

/**
 * Get all Reddit posts (leads) from MongoDB.
 * @param {Object} filters - Optional { subreddit, status, limit }
 * @returns {Promise} { posts, total }
 */
export async function getLeads(filters = {}) {
  const params = new URLSearchParams();
  if (filters.subreddit) params.append('subreddit', filters.subreddit);
  if (filters.status) params.append('status', filters.status);
  if (filters.limit) params.append('limit', filters.limit);
  const response = await fetch(`${PROXY_HOST}/api/reddit/posts?${params}`, {
    headers: proxyHeaders,
  });
  if (!response.ok) throw new Error('Failed to get leads');
  const data = await response.json();
  // Map to lead-compatible shape for backward compat with Vuex store
  return (data.posts || []).map(p => ({
    id: p.id,
    sourceId: p.redditId,
    sourceUrl: p.url,
    sourceTitle: p.title,
    sourceBody: p.body,
    sourceAuthor: p.author,
    sourceSubreddit: p.subreddit,
    channel: 'reddit',
    status: p.status,
    petInfo: p.petInfo,
    assessmentId: p.assessmentId,
    shareUrl: p.shareUrl,
    hookMessage: p.hookMessage,
    awardedAt: p.awardedAt,
    cantRewardReason: p.cantRewardReason,
    score: p.score,
    numComments: p.numComments,
    createdUtc: p.createdUtc,
    media: p.media,
    fetchedAt: p.fetchedAt,
  }));
}

/**
 * Update a Reddit post's status.
 * @param {string} id - MongoDB post ID
 * @param {Object} data - { status, cantRewardReason }
 * @returns {Promise} Updated post
 */
export async function updateLead(id, data) {
  // If data has diagnosis fields, use the diagnosis endpoint
  if (data.assessmentId || data.diagnosis || data.petInfo) {
    const response = await fetch(`${PROXY_HOST}/api/reddit/posts/${id}/diagnosis`, {
      method: 'PATCH',
      headers: proxyHeaders,
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to save diagnosis');
    return response.json();
  }

  // Otherwise use the status endpoint
  const response = await fetch(`${PROXY_HOST}/api/reddit/posts/${id}/status`, {
    method: 'PATCH',
    headers: proxyHeaders,
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to update status');
  return response.json();
}

/**
 * Delete a Reddit post (lead).
 * Not actually needed — we mark as not_relevant instead.
 * Kept for backward compat.
 * @param {string} id - MongoDB post ID
 */
export async function deleteLead(id) {
  return updateLead(id, { status: 'not_relevant' });
}

/**
 * Get Reddit pipeline stats.
 * @returns {Promise} { total, new, relevant, diagnosed, rewarded, cant_reward, not_relevant }
 */
export async function getRedditStats() {
  const response = await fetch(`${PROXY_HOST}/api/reddit/stats`, {
    headers: proxyHeaders,
  });
  if (!response.ok) throw new Error('Failed to get stats');
  return response.json();
}

// ==========================================
// Subreddit Config (adaptive fetch rates)
// ==========================================

/**
 * Get all subreddit configs with measured rates.
 * @returns {Promise} Array of subreddit configs
 */
export async function getSubredditConfigs() {
  const response = await fetch(`${PROXY_HOST}/api/reddit/subreddit-configs`, {
    headers: proxyHeaders,
  });
  if (!response.ok) throw new Error('Failed to get subreddit configs');
  return response.json();
}

/**
 * Update a subreddit config.
 * @param {string} subreddit - Subreddit name
 * @param {Object} data - { enabled, petType, fetchLimit, fetchIntervalMinutes }
 */
export async function updateSubredditConfig(subreddit, data) {
  const response = await fetch(`${PROXY_HOST}/api/reddit/subreddit-configs/${subreddit}`, {
    method: 'PATCH',
    headers: proxyHeaders,
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to update subreddit config');
  return response.json();
}

/**
 * Get fetch schedule — which subreddits are due.
 * @returns {Promise} Array of { subreddit, overdue, overdueMinutes, nextFetchAt, ... }
 */
export async function getFetchSchedule() {
  const response = await fetch(`${PROXY_HOST}/api/reddit/fetch-schedule`, {
    headers: proxyHeaders,
  });
  if (!response.ok) throw new Error('Failed to get fetch schedule');
  return response.json();
}

// ==========================================
// Background Scheduler
// ==========================================

export async function startScheduler() {
  const response = await fetch(`${PROXY_HOST}/api/reddit/scheduler/start`, {
    method: 'POST',
    headers: proxyHeaders,
  });
  if (!response.ok) throw new Error('Failed to start scheduler');
  return response.json();
}

export async function stopScheduler() {
  const response = await fetch(`${PROXY_HOST}/api/reddit/scheduler/stop`, {
    method: 'POST',
    headers: proxyHeaders,
  });
  if (!response.ok) throw new Error('Failed to stop scheduler');
  return response.json();
}

export async function getSchedulerStatus() {
  const response = await fetch(`${PROXY_HOST}/api/reddit/scheduler/status`, {
    headers: proxyHeaders,
  });
  if (!response.ok) throw new Error('Failed to get scheduler status');
  return response.json();
}
